import React, { CSSProperties } from "react";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { FiltersBar } from "@/features/orders-analytics/components/FiltersBar";
import { useAvailableClients } from "@/features/orders-analytics/hooks/use-available-clients";
import { useCorridorsData } from "@/features/orders-analytics/hooks/use-corridors-data";
import { useOrdersAnalyticsFilters } from "@/features/orders-analytics/hooks/use-orders-analytics-filters";
import { SectionHeader } from "@/shell/components/SectionHeader";
import { StatusBanner } from "@/shell/components/StatusBanner";
import { corridorsViewModel } from "@/features/orders-analytics/pages/corridors-view-model";

const successCellStyle = (success: string): CSSProperties => ({
    color: success !== "—" && parseFloat(success) < 90 ? "var(--vb-error-500)" : "var(--vb-fg)",
    fontWeight: 600,
});

const formatConcentration = (value: number | null): string => (value === null ? "—" : `${value.toFixed(1)}%`);

export function CorridorsPage() {
    const { filters, setFilters } = useOrdersAnalyticsFilters();
    const { availableClients, loading, refresh } = useAvailableClients(filters);
    const { data } = useCorridorsData(filters);

    const vm = corridorsViewModel.fromData({
        segmentation: data.segmentation,
        customerIntelligence: data.customerIntelligence,
    });

    return (
        <Shell title="Corridors & clients" subtitle="Network performance">
            <PageHeader
                title="Corridors, clients & providers"
                lede="Network view: where the business runs, who carries it, who depends on what."
                right={
                    <FiltersBar
                        filters={filters}
                        availableClients={availableClients}
                        onFiltersChange={setFilters}
                        onRefresh={refresh}
                        loading={loading}
                    />
                }
            />

            {vm.concentration.alertSeverity === null ? null : (
                <StatusBanner
                    severity={vm.concentration.alertSeverity}
                    title={`Top account = ${formatConcentration(vm.concentration.top1)} of revenue`}
                    sub={`Top 3 = ${formatConcentration(vm.concentration.top3)} · Top 5 = ${formatConcentration(vm.concentration.top5)} · Non-top-account growth ${vm.concentration.nonTopGrowth} · revenue ex-top ${vm.concentration.revExTop1}`}
                />
            )}

            <section className="section">
                <SectionHeader
                    title="Corridor matrix"
                    count={vm.corridors.length}
                    meta="volume · revenue · success · P95 · take rate"
                />
                {vm.corridors.length === 0 ? (
                    <div className="td-muted" style={emptySectionStyle}>
                        Sin datos disponibles — no hay fuente real para esta sección.
                    </div>
                ) : (
                    <div className="tbl-wrap">
                        <table className="tbl">
                            <thead>
                                <tr>
                                    <th>Corridor</th>
                                    <th className="td-num">Volume</th>
                                    <th className="td-num">Revenue</th>
                                    <th className="td-num">Orders</th>
                                    <th className="td-num">Success</th>
                                    <th className="td-num">P95</th>
                                    <th className="td-num">Take rate</th>
                                    <th>Risk</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vm.corridors.map((corridor) => (
                                    <tr
                                        key={corridor.name}
                                        className={corridor.risk === "critical" ? "row--critical" : ""}
                                    >
                                        <td className="td-strong mono" style={corridorNameStyle}>
                                            {corridor.name}
                                        </td>
                                        <td className="td-num td-strong">{corridor.volume}</td>
                                        <td className="td-num">{corridor.revenue}</td>
                                        <td className="td-num">{corridor.orders.toLocaleString()}</td>
                                        <td className="td-num" style={successCellStyle(corridor.success)}>
                                            {corridor.success}
                                        </td>
                                        <td className="td-num">{corridor.p95}</td>
                                        <td className="td-num td-strong">{corridor.takeRate}</td>
                                        <td>
                                            <span className={`sev-chip sev-chip--${corridor.risk}`}>
                                                <span className={`sev-dot sev-dot--${corridor.risk}`} />
                                                {corridor.risk}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="section">
                <div className="grid-2">
                    <div>
                        <SectionHeader
                            title="Provider ranking"
                            meta="withdraw/onramp provider names are raw vendor identifiers in the index"
                        />
                        <div className="td-muted" style={emptySectionStyle}>
                            Sin datos disponibles — no hay fuente real para esta sección (los nombres de
                            provider son identificadores de vendor sin tipo abstracto; no se exponen).
                        </div>
                    </div>

                    <div>
                        <SectionHeader
                            title="Client ranking"
                            count={vm.clients.length}
                            meta="revenue share · growth"
                        />
                        {vm.clients.length === 0 ? (
                            <div className="td-muted" style={emptySectionStyle}>
                                Sin datos disponibles — no hay fuente real para esta sección.
                            </div>
                        ) : (
                            <div className="tbl-wrap">
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th>Client</th>
                                            <th className="td-num">Volume</th>
                                            <th className="td-num">Revenue</th>
                                            <th className="td-num">Share</th>
                                            <th className="td-num">Orders</th>
                                            <th className="td-num">Success</th>
                                            <th className="td-num">Growth</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vm.clients.map((client, index) => (
                                            <tr
                                                key={client.name}
                                                className={index === 0 ? "row--critical" : ""}
                                            >
                                                <td className="td-strong">{client.name}</td>
                                                <td className="td-num">{client.volume}</td>
                                                <td className="td-num td-strong">{client.revenue}</td>
                                                <td className="td-num">
                                                    <div style={shareCellStyle}>
                                                        <div style={shareBarOuterStyle}>
                                                            <div
                                                                style={{
                                                                    ...shareBarInnerStyle,
                                                                    width: `${client.share}%`,
                                                                    background:
                                                                        index === 0
                                                                            ? "var(--vb-error-500)"
                                                                            : "var(--vb-primary-500)",
                                                                }}
                                                            />
                                                        </div>
                                                        <span className="td-strong">
                                                            {client.share.toFixed(1)}%
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="td-num">{client.orders.toLocaleString()}</td>
                                                <td className="td-num">{client.success}</td>
                                                <td className="td-num td-muted">{client.growth}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </Shell>
    );
}

const emptySectionStyle: CSSProperties = { padding: "16px 4px", fontSize: 13 };

const corridorNameStyle: CSSProperties = { fontSize: 12 };

const shareCellStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 6,
};

const shareBarOuterStyle: CSSProperties = {
    width: 60,
    height: 6,
    background: "var(--vb-neutral-100)",
    borderRadius: 3,
    overflow: "hidden",
};

const shareBarInnerStyle: CSSProperties = {
    height: "100%",
};
